/**
 * (?) icon next to jargon words; opens a popover whose example values are
 * taken from the surrounding claim/promo so users see real numbers, not
 * generic placeholders.
 *
 * Usage:
 *   <TermTooltip term="turnover" claim={c} promo={p} />
 */
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/datetime/format.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import { bigIntish, type PromotionClaim, type Promotion } from '@/features/promo/api.js';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover.js';

type Term = 'turnover' | 'bonus' | 'cash' | 'unlock' | 'expiry' | 'multiplier' | 'wagering';

interface Props {
  term: Term;
  /**
   * REQUIRED. Every illustrated number comes from the claim, so there is no
   * shape of this component that renders without one.
   */
  claim: PromotionClaim;
  /** Multiplier source for the `{{mult}}` placeholder in `turnover`/`multiplier`. */
  promo?: Promotion | null;
  currency?: string;
}

/**
 * Which source each illustrated number comes from — all three from the claim.
 *
 * There WAS a pre-claim half here, reading the amount out of the promotion's
 * reward rules and applying the multiplier itself. It could not run: the
 * component's only call site is `ActiveBonusHero`, inside
 * `claims.filter(c => turnoverRequired > 0n).map(...)`, so `claim` is always a
 * real claim and `promo` was never passed by anyone. Every ternary took the
 * claim branch in production while six tests policed the branch that did not,
 * which reads as coverage and is not (W10). The pre-claim wagering target IS
 * disclosed to the player — by `PromoCard`'s `infoLines`, per promo kind and
 * with the real multiplier, which is a better answer than this branch was.
 *
 * Exported so the source choice itself is testable: recomputing the target here
 * rather than reading the server's `turnoverRequired` is how the tooltip and the
 * progress bar come to disagree on the same screen.
 */
export function tooltipAmounts(
  claim: PromotionClaim,
): { bonus: bigint; target: bigint; achieved: bigint } {
  return {
    bonus: bigIntish(claim.grantedAmount ?? claim.amount),
    target: bigIntish(claim.turnoverRequired),
    achieved: bigIntish(claim.turnoverAchieved),
  };
}

export function TermTooltip({ term, claim, promo, currency = 'THB' }: Props) {
  const { t, i18n } = useTranslation('promo');
  const formatMoney = useFormatMoney();

  const cur = claim.currency ?? currency;
  const { bonus, target, achieved } = tooltipAmounts(claim);
  const exprDate = claim.expiresAt
    ? formatDate(claim.expiresAt, i18n.language)
    : '';

  const params: Record<string, string | number> = {};
  // Only when a caller actually supplies the promotion. A default here renders
  // a made-up multiplier into `tooltip.turnover.example` and
  // `tooltip.multiplier.example` as though it were the promotion's own.
  if (promo?.turnoverMultiplier) params.mult = Number(promo.turnoverMultiplier);
  if (bonus > 0n) {
    params.bonus = formatMoney(bonus, cur);
    params.amount = formatMoney(bonus, cur);
  }
  if (target > 0n) {
    params.target = formatMoney(target, cur);
    params.required = formatMoney(target, cur);
  }
  params.achieved = formatMoney(achieved, cur);
  if (exprDate) params.date = exprDate;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full bg-[var(--bg-elevated)] text-[var(--text-muted)] text-[9px] font-bold hover:bg-[var(--primary)] hover:text-[var(--primary-on)]"
          aria-label={`Explain ${term}`}
        >?</button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[240px] rounded-lg border border-white/10 p-3 shadow-2xl text-white"
        style={{ backgroundColor: 'rgba(15, 23, 42, 0.98)', backdropFilter: 'blur(8px)' }}
      >
        <div className="text-[11.5px] font-bold text-[#fbbf24]">
          {t(`tooltip.${term}.title`)}
        </div>
        <div className="text-[11px] text-white/85 mt-1">
          {t(`tooltip.${term}.body`)}
        </div>
        <div className="text-[10.5px] text-white/65 mt-2">
          {t(`tooltip.${term}.example`, params)}
        </div>
      </PopoverContent>
    </Popover>
  );
}
